import React from "react"
import { Typography, TypographyProps } from "@mui/material"

// Sizes (from theme.scss)
const sizeStyles = {
  xxl: { fontSize: "clamp(24px, 3vw, 32px)", lineHeight: "1.2" },
  xl:  { fontSize: "clamp(20px, 2.5vw, 24px)", lineHeight: "1.2" },
  l:   { fontSize: "clamp(18px, 2.2vw, 22px)", lineHeight: "1.2" },
  lg:  { fontSize: "clamp(17px, 2vw, 20.7px)", lineHeight: "1.2" },
  md:  { fontSize: "clamp(16px, 1.8vw, 18px)", lineHeight: "1.4" },
  sm:  { fontSize: "clamp(14px, 1.6vw, 16px)", lineHeight: "1.4" },
  xs:  { fontSize: "clamp(13px, 1.4vw, 14px)", lineHeight: "1.4" },
  xxs: { fontSize: "clamp(11px, 1.2vw, 12px)", lineHeight: "1.4" },
} as const


// Colors (from theme.scss)
const colors = {
  text: "#2E2E52",         // $lxDefaultFontColor
  disabled: "#898989",     // $luxaiGrayDisabled
}

// Font weights (can expand if you need bold/semibold mapping)
const fontWeightStyles = {
  normal: { fontWeight: 400 },
  medium: { fontWeight: 500 },
  bold: { fontWeight: 700 },
} as const

type Sizes = keyof typeof sizeStyles
type Weights = keyof typeof fontWeightStyles
type Presets =
  | "default"
  | "bold"
  | "heading"
  | "subheading"
  | "formLabel"
  | "formHelper"
  | "pollHeading"
  | "pollSubHeading"

export interface TextProps extends TypographyProps {
  text?: string
  preset?: Presets
  weight?: Weights
  size?: Sizes
  children?: React.ReactNode
}

const baseStyle: React.CSSProperties = {
  ...sizeStyles.sm,
  ...fontWeightStyles.normal,
  color: colors.text,
}

// Preset definitions
const presets: Record<Presets, React.CSSProperties | React.CSSProperties[]> = {
  default: baseStyle,
  bold: [{ ...baseStyle }, fontWeightStyles.bold],
  heading: [{ ...baseStyle }, sizeStyles.xl, fontWeightStyles.bold],
  subheading: [{ ...baseStyle }, sizeStyles.md, { color: colors.disabled }, fontWeightStyles.normal],
  formLabel: [{ ...baseStyle }, fontWeightStyles.normal],
  formHelper: [{ ...baseStyle }, sizeStyles.sm, fontWeightStyles.normal],
  pollHeading: [{ ...baseStyle }, sizeStyles.xl],
  pollSubHeading: [{ ...baseStyle }, sizeStyles.md, { color: colors.disabled }, fontWeightStyles.normal],
}

export function PollText(props: TextProps) {
  const { weight, size, text, children, preset, style, ...rest } = props

  const content = text || children
  const resolvedPreset = presets[preset || "default"]

  const finalStyles: React.CSSProperties = {
    ...(Array.isArray(resolvedPreset) ? Object.assign({}, ...resolvedPreset) : resolvedPreset),
    ...(weight ? fontWeightStyles[weight] : {}),
    ...(size ? sizeStyles[size] : {}),
    ...(style as React.CSSProperties),
  }

  return (
    <Typography {...rest} style={finalStyles}>
      {content}
    </Typography>
  )
}
